/**
 * Send a meter photo as soon as it is chosen and show it straight away.
 *
 * Waiting for the submit meant the customer had no idea whether the picture had
 * arrived, and a failed submit lost the file: a browser cannot refill a file
 * field. The photo is parked on the server right away and the form only carries
 * the name it was parked under.
 */

/**
 * @returns {string} the CSRF token, or empty when the meta tag is gone
 */
function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');

    return meta ? meta.content : '';
}

/**
 * Show a photo over the page instead of growing it in place: enlarging it
 * between the fields pushed everything below it out of view.
 */
/**
 * @param {string} source - the image url
 * @param {string} alt - its alternative text
 */
function openLightbox(source, alt) {
    const overlay = document.createElement('div');
    overlay.className = 'reading-photo-lightbox';

    const image = document.createElement('img');
    image.src = source;
    image.alt = alt;

    /** Take the overlay down and stop listening for the escape key. */
    function close() {
        overlay.remove();
        document.removeEventListener('keydown', onKey);
    }

    /** @param {KeyboardEvent} event */
    function onKey(event) {
        if (event.key === 'Escape') {
            close();
        }
    }

    overlay.appendChild(image);
    overlay.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);
}

/**
 * @param {File} file - the chosen photo
 * @returns {Promise<string>} a data url to show it with
 */
function readPreview(file) {
    return new Promise(function (resolve) {
        const reader = new FileReader();

        reader.onload = function () {
            resolve(reader.result);
        };
        reader.onerror = function () {
            resolve('');
        };
        reader.readAsDataURL(file);
    });
}

/**
 * @param {File} file
 * @returns {string} what makes two choices the same photo: name, size and date
 */
function photoKey(file) {
    return file.name + ':' + file.size;
}

/**
 * Take away the note about an already added photo.
 *
 * Called when a new choice is made: from then on the note says nothing about
 * what is in the field.
 */
/**
 * @param {HTMLElement} holder - the field the note belongs to
 */
function clearDuplicateMessage(holder) {
    const message = (holder ? holder.querySelector('.reading-photo-duplicate') : null);

    if (message) {
        message.remove();
    }
}

/**
 * Say that a photo was already there, so a dropped choice does not look like a
 * field that simply ignored the click. It stays until the next choice.
 */
/**
 * @param {HTMLElement} holder - the field the note belongs to
 * @param {string[]} names - the photos that were dropped
 */
function showDuplicateMessage(holder, names) {
    // the file name is set in bold, so it is clear at a glance which picture was
    // refused; it comes from the customer's own disk, hence the escaping
    const escaped = names.map(function (name) {
        const escape = document.createElement('span');
        escape.textContent = name;

        return '<span style="font-weight: bold;">' + escape.innerHTML + '</span>';
    });

    const text = (holder.dataset.duplicateMessage || '').replace(':name', escaped.join(', '));

    if (!text) {
        return;
    }

    let message = holder.querySelector('.reading-photo-duplicate');

    if (!message) {
        message = document.createElement('div');
        message.className = 'reading-photo-duplicate';
        holder.appendChild(message);
    }

    message.innerHTML = text;
}

/**
 * Take out of this field what it, or another field on the page, already holds.
 *
 * The same picture twice says nothing extra, and here the fields sit next to
 * each other: the meter photo and the extra photos are easily the same file.
 * The selection is rebuilt, which is the only way to change a file field.
 */
/**
 * @param {HTMLInputElement} input - the file field to rebuild
 * @returns {boolean} whether anything was dropped
 */
function dropDuplicates(input) {
    if (typeof DataTransfer === 'undefined') {
        return;
    }

    const taken = [];

    document.querySelectorAll('.reading-photos .reading-photo-input').forEach(function (other) {
        if (other !== input) {
            Array.from(other.files || []).forEach(function (file) {
                taken.push(photoKey(file));
            });
        }
    });

    const keep = new DataTransfer();
    const dropped = [];

    Array.from(input.files || []).forEach(function (file) {
        const key = photoKey(file);

        if (taken.includes(key)) {
            dropped.push(file.name);

            return;
        }

        taken.push(key);
        keep.items.add(file);
    });

    if (dropped.length) {
        input.files = keep.files;
        showDuplicateMessage(input.closest('.reading-photos'), dropped);
    }
}

/**
 * Fields that have nowhere to send a photo yet only show what was chosen.
 *
 * The readings page creates its reading flow while submitting, so there is no
 * record to attach a photo to beforehand; the file travels with the form as it
 * always did, and this only replaces the browser's own "no file chosen".
 */
/**
 * @param {HTMLElement} holder - the field wrapper on the single readings page
 */
function initSimplePhotos(holder) {
    const input = holder.querySelector('.reading-photo-input');
    const previews = holder.querySelector('.reading-photo-previews');

    if (!input || !previews) {
        return;
    }

    input.addEventListener('change', function () {
        // a fresh choice: whatever the note said no longer applies
        clearDuplicateMessage(holder);
        dropDuplicates(input);
        previews.innerHTML = '';

        Array.from(input.files || []).forEach(function (file) {
            readPreview(file).then(function (source) {
                if (!source) {
                    return;
                }

                const item = document.createElement('div');
                item.className = 'reading-photo';

                const image = document.createElement('img');
                image.alt = file.name;
                image.src = source;
                image.addEventListener('click', function () {
                    openLightbox(source, file.name);
                });

                item.appendChild(image);
                previews.appendChild(item);
            });
        });
    });
}

/**
 * @param {HTMLElement} holder - the field wrapper of one row
 */
function initPhotos(holder) {
    const input = holder.querySelector('.reading-photo-input');
    const previews = holder.querySelector('.reading-photo-previews');

    if (!input || !previews) {
        return;
    }

    const url = holder.dataset.uploadUrl;
    const flowId = holder.dataset.flowId;
    const product = holder.dataset.product;

    /**
     * @param {File} file - the chosen photo
     * @param {string} name - the name it was parked under
     * @param {string} source - a data url to show it with
     */
    function addPreview(file, name, source) {
        const item = document.createElement('div');
        item.className = 'reading-photo';
        // what makes a photo the same one for this field
        item.dataset.photoKey = file.name + ':' + file.size;

        const image = document.createElement('img');
        image.alt = file.name;
        image.src = source;

        // a meter reading is small on a photo; clicking gives a closer look
        image.addEventListener('click', function () {
            openLightbox(source, file.name);
        });

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'reading-photo-remove';
        remove.setAttribute('aria-label', file.name);
        remove.textContent = '\u00d7';

        const stored = document.createElement('input');
        stored.type = 'hidden';
        stored.name = 'rows[' + flowId + '][photo_names][' + product + '][]';
        stored.value = name;

        remove.addEventListener('click', function () {
            item.remove();
        });

        item.appendChild(image);
        item.appendChild(remove);
        item.appendChild(stored);
        previews.appendChild(item);
    }

    /** @param {string} message - what went wrong, empty to clear it */
    function showError(message) {
        const error = document.createElement('div');
        error.className = 'reading-photo-error invalid-feedback d-block';
        error.textContent = message;
        previews.appendChild(error);
    }

    input.addEventListener('change', function () {
        // the same picture twice says nothing extra, and a customer picking the
        // same file again is correcting a mistake, not adding one
        // a fresh choice: whatever the note said no longer applies
        clearDuplicateMessage(holder);

        const chosen = [];
        const dropped = [];
        const files = Array.from(input.files || []).filter(function (file) {
            const key = photoKey(file);

            if (chosen.includes(key) || previews.querySelector('[data-photo-key="' + CSS.escape(key) + '"]')) {
                dropped.push(file.name);

                return false;
            }

            chosen.push(key);

            return true;
        });

        if (dropped.length) {
            showDuplicateMessage(holder, dropped);
        }

        if (!files.length) {
            input.value = '';

            return;
        }

        holder.classList.add('is-uploading');

        Promise.all(files.map(function (file) {
            const data = new FormData();
            data.append('flow_id', flowId);
            data.append('product', product);
            data.append('photo', file);

            return fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': csrfToken(),
                },
                body: data,
            }).then(function (response) {
                return response.json().catch(function () {
                    return null;
                });
            }).then(function (result) {
                if (result && result.name) {
                    return readPreview(file).then(function (source) {
                        addPreview(file, result.name, source);
                    });
                }

                showError((result && result.error) ? result.error : file.name);
            }).catch(function () {
                showError(file.name);
            });
        })).then(function () {
            holder.classList.remove('is-uploading');
            // the files are on the server; leaving them in the field would send
            // them a second time on submit
            input.value = '';
        });
    });
}

document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.reading-photos[data-upload-url]').forEach(initPhotos);
    document.querySelectorAll('.reading-photos:not([data-upload-url])').forEach(initSimplePhotos);
});
